<template>
  <!-- 선언된 sub 전부: [provider 아이콘] + 아래 [상태]. -->
  <!-- 3모드 — always: 항상 표시 / error: 비정상 소스가 있을 때만 / hidden: 숨김 -->
  <div v-if="visible" class="sub-status pos-abs d-flex justify-center" style="width: 100%; z-index: 2">
    <div class="d-flex" style="gap: 14px">
      <div v-for="s in statusList" :key="s.provider" class="sub-status__item d-flex flex-column align-center">
        <v-icon v-if="s.mdi" :size="28" :color="s.mdiColor">{{ s.mdi }}</v-icon>
        <component v-else :is="s.icon" size="28"></component>
        <span class="sub-status__state" :style="{ color: s.color }">{{ s.text }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VirtupetIcon from "@/components/VirtupetIcon.vue";
import VChzzkIcon from "@/components/icons/VChzzkIcon.vue";
import VTwitchIcon from "@/components/icons/VTwitchIcon.vue";
import VAfreecaIcon from "@/components/icons/VAfreecaIcon.vue";

// provider → 아이콘 컴포넌트 (virtupet=내부 제어, 나머지=플랫폼)
const PROVIDER_ICON = {
  virtupet: "VirtupetIcon",
  chzzk: "VChzzkIcon",
  twitch: "VTwitchIcon",
  afreeca: "VAfreecaIcon",
};

// 브랜드 컴포넌트 대신 mdi 아이콘으로 그릴 provider. { provider: { icon, color } }
const PROVIDER_MDI = {
  chzzk_relay: { icon: "mdi-radio-tower", color: "#7C4DFF" }, // 관리자 relay = 중계(전파탑)
};

// 상태바 표시 순서 = 방송관리 탭 순서(virtupet 제어 먼저, relay 맨 뒤). 목록에 없는 provider는 뒤로.
const PROVIDER_ORDER = ["virtupet", "chzzk", "twitch", "soop", "youtube", "afreeca", "chzzk_relay"];

// 상태 → 아래 표시 텍스트/색
// 색상은 크로마키(연두색) 보색 계열(보라/마젠타) 위주로 — 송출 화면에서 눈에 잘 띄게.
const STATUS_DISPLAY = {
  idle: { text: "대기중", color: "#546E7A" }, // open 전(소켓 없음)
  unconfigured: { text: "설정안됨", color: "#9E9E9E" }, // 미설정 후보(dev 페이지에서만 노출)
  connecting: { text: "연결중", color: "#FF6D00" }, // 연결 시도 중(주황)
  ready: { text: "연결됨", color: "#7C4DFF" }, // 소켓 연결됨, 처리 전(보라)
  processing: { text: "처리중", color: "#E91E63" }, // StreamEvent 실제 처리 중(마젠타 — 보색, 가장 눈에 띔)
  offline: { text: "방송중 아님", color: "#78909C" }, // 채널은 있으나 방송 오프라인(실패 아님 — chzzk_relay)
  failed: { text: "실패", color: "#FF1744" }, // 연결/구독 실패(채널없음·에러)
  closed: { text: "끊김", color: "#D50000" }, // 상대(서버)가 거부/끊음
};

// failed 원인 코드(Source.statusDetail) → 표시 라벨. 기술 코드는 그대로 병기.
//  E_5XX = HTTP 응답은 오는데 읽기 차단(CF 에러 페이지 — 서버측 장애 추정)
//  E_NETWORK = 응답 자체 없음(DNS/오프라인), E_SOCKET = HTTP 정상인데 소켓만 실패(프록시 WS 설정 의심)
//  HTTP_xxx = 서버가 직접 반환한 코드 (예: HTTP_500)
const FAIL_DETAIL_DISPLAY = {
  E_5XX: "서버 장애 (E_5XX)",
  E_NETWORK: "네트워크 오류 (E_NETWORK)",
  E_SOCKET: "연결 실패 (E_SOCKET)",
};
function failDetailText(detail) {
  if (!detail) return null;
  if (FAIL_DETAIL_DISPLAY[detail]) return FAIL_DETAIL_DISPLAY[detail];
  if (detail.startsWith("HTTP_")) return `서버 장애 (${detail.replace("_", " ")})`;
  return `실패 (${detail})`;
}

// BroadcastBox 전용 sub 상태 표시. statuses 맵의 각 sub를 [아이콘]+[상태]로.
//  소켓 축(idle/connecting/ready/failed/closed) + 처리(processing). closed=상대 끊김만.
//  - statuses: { [provider]: "idle" | "connecting" | "ready" | "processing" | "failed" | "closed" }
//  - mode: "always"(항상) | "error"(에러시에만) | "hidden"(숨김)
export default {
  components: { VirtupetIcon, VChzzkIcon, VTwitchIcon, VAfreecaIcon },
  props: {
    statuses: { type: Object, default: () => ({}) },
    details: { type: Object, default: () => ({}) }, // failed 원인 코드 { [provider]: E_5XX ... }
    mode: { type: String, default: "error" },
  },
  computed: {
    statusList() {
      const orderOf = (p) => {
        const i = PROVIDER_ORDER.indexOf(p);
        return i === -1 ? PROVIDER_ORDER.length : i;
      };
      return Object.keys(this.statuses)
        .map((provider) => {
          const status = this.statuses[provider];
          const d = STATUS_DISPLAY[status] || { text: status, color: "#9e9e9e" };
          // failed면 진단 원인 코드 병기 (예: "서버 장애 (E_5XX)")
          const detailText = status === "failed" ? failDetailText(this.details[provider]) : null;
          const mdi = PROVIDER_MDI[provider] || null;
          return {
            provider,
            status,
            icon: PROVIDER_ICON[provider] || "VirtupetIcon",
            mdi: mdi ? mdi.icon : null,
            mdiColor: mdi ? mdi.color : null,
            text: detailText || d.text,
            color: d.color,
          };
        })
        .sort((a, b) => orderOf(a.provider) - orderOf(b.provider)); // 탭 순서로 고정
    },
    visible() {
      if (!this.statusList.length) return false;
      if (this.mode === "always") return true;
      if (this.mode === "hidden") return false;
      // error: 정상(ready/processing)이 아닌 소스가 하나라도 있으면 표시
      return this.statusList.some((s) => s.status !== "ready" && s.status !== "processing");
    },
  },
};
</script>

<style scoped>
.sub-status__state {
  font-size: 11px;
  margin-top: 2px;
  line-height: 1;
}
</style>
